import { Link } from "react-router-dom";
import { ShieldCheck, Columns3, ClipboardCheck } from "lucide-react";
import Seo from "@/components/site/Seo";
import HomeHero from "@/components/site/HomeHero";
import CategoryCard from "@/components/site/CategoryCard";
import SoftwareCard from "@/components/site/SoftwareCard";
import Newsletter from "@/components/site/Newsletter";
import { ContentCard, Empty, SkeletonGrid } from "@/components/site/Ui";
import { useApi } from "@/lib/api";
import { useSite } from "@/lib/site";

const Section = ({ eyebrow, title, intro, action, children, testId, band }) => (
  <section data-testid={testId} className={band ? "bg-white border-y border-[#DCE5E8]" : ""}>
    <div className="container-x section">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10 md:mb-12">
        <div className="max-w-2xl">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-[2.5rem] lg:leading-[1.15] font-bold">{title}</h2>
          {intro && <p className="mt-3 text-[15px] text-[#526174] leading-relaxed">{intro}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  </section>
);

const WHY = [
  [ClipboardCheck, "Research software with confidence", "Profiles built from official vendor information, with clear dates showing when pricing was last checked and plain notes when it hasn't been."],
  [Columns3, "Compare solutions side by side", "Pin up to four tools from anywhere on the site and see features, pricing models and best-fit use cases in one table."],
  [ShieldCheck, "Make informed buying decisions", "Practical guides, transparent affiliate disclosures and an editorial policy that keeps commercial relationships separate from recommendations."],
];

export default function Home() {
  const { categories } = useSite();
  const featured = useApi("/software?featured=true&limit=6");
  const latest = useApi("/latest?limit=6");
  return (
    <>
      <Seo jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", name: "SaaSPivora", url: "https://saaspivora.com",
        potentialAction: { "@type": "SearchAction", target: "https://saaspivora.com/software?q={query}", "query-input": "required name=query" } }} />
      <HomeHero />
      <Section eyebrow="Categories" title="Browse software by business need" testId="category-grid"
        intro="Ten focused categories covering the tools most businesses evaluate."
        action={<Link to="/categories" className="btn-secondary" data-testid="home-all-categories">All categories</Link>}>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {categories.map((c, i) => <CategoryCard key={c.slug} c={c} i={i} testId={`category-card-${c.slug}`} />)}
        </div>
      </Section>
      <Section band eyebrow="Featured software" title="Widely used tools worth knowing" testId="featured-software"
        intro="Factual profiles of established products. Pricing appears only once it has been checked on the official website."
        action={<Link to="/software" className="btn-secondary" data-testid="home-view-directory">View directory</Link>}>
        {featured.loading ? <SkeletonGrid /> : featured.data?.items.length ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{featured.data.items.map((s) => <SoftwareCard key={s.slug} s={s} />)}</div>
        ) : <Empty title="No featured software yet" />}
      </Section>
      <Section eyebrow="Why SaaSPivora?" title="Research built for real buying decisions" testId="why-saaspivora-section">
        <div className="grid gap-6 md:grid-cols-3">
          {WHY.map(([Icon, t, d], i) => (
            <div key={t} className="card p-8 relative overflow-hidden">
              <span className="absolute top-6 right-7 font-display text-5xl font-extrabold text-[#EEF2F4] select-none" aria-hidden="true">0{i + 1}</span>
              <span className="icon-tile h-12 w-12 relative"><Icon className="h-6 w-6" /></span>
              <h3 className="mt-6 text-xl font-semibold relative">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#526174] relative">{d}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section band eyebrow="Latest insights" title="Reviews, comparisons and guides" testId="latest-insights"
        action={<Link to="/blog" className="btn-secondary" data-testid="home-view-blog">Visit the blog</Link>}>
        {latest.loading ? <SkeletonGrid /> : latest.data?.length ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{latest.data.map((it, i) => <ContentCard key={it.href} item={it} testId={`latest-card-${i}`} />)}</div>
        ) : <Empty title="New content is on the way" />}
      </Section>
      <div className="pt-16 md:pt-24 pb-8"><Newsletter /></div>
    </>
  );
}
